import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';

import { ChatStorageService, HttpWrapperService } from '@craig/core';
import { ChatRole } from '@app/enums';
import { ChatMessage, ChatBotResponse } from '@app/interfaces';
import { ResponseFeedbackPayload } from '../../interfaces/response-feedback.interface';

@Injectable()
export class ChatService {
    private readonly responseLoading$: Subject<boolean> = new Subject<boolean>();
    private readonly newMessage$: BehaviorSubject<ChatMessage | undefined> = new BehaviorSubject<
        ChatMessage | undefined
    >(undefined);

    constructor(
        private readonly httpWrapperService: HttpWrapperService,
        private readonly chatStorageService: ChatStorageService
    ) {}

    sendQuery(message: ChatMessage): Observable<ChatBotResponse | null> {
        const messages: ChatMessage[] = this.chatStorageService.getCurrentChatMessages();

        return this.httpWrapperService
            .post<ChatBotResponse>('/api/chat', [...messages, message])
            .pipe(map(({ body }: HttpResponse<ChatBotResponse>) => body));
    }

    sendResponseFeedback(payload: ResponseFeedbackPayload): Observable<null> {
        return this.httpWrapperService
            .post<null>('/api/feedback', payload)
            .pipe(map(({ body }: HttpResponse<null>) => body));
    }

    showResponseLoader(): void {
        this.responseLoading$.next(true);
    }

    hideResponseLoader(): void {
        this.responseLoading$.next(false);
    }

    responseLoadingChange(): Observable<boolean> {
        return this.responseLoading$.asObservable();
    }

    newMessageChange(): Observable<ChatMessage | undefined> {
        return this.newMessage$.asObservable();
    }

    addUserMessage(message: string): ChatMessage {
        const userMessage: ChatMessage = this.createMessage(message, ChatRole.User);

        this.newMessage$.next(userMessage);

        return userMessage;
    }

    addAssistantMessage(message: string, requestId?: string): ChatMessage {
        const assistantMessage: ChatMessage = this.createMessage(message, ChatRole.Assistant, requestId);

        this.newMessage$.next(assistantMessage);

        return assistantMessage;
    }

    private createMessage(content: string, role: ChatRole, requestId?: string): ChatMessage {
        return {
            content,
            role,
            requestId
        };
    }
}
